import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { setAuthToken } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, UserCog, Activity, Users, LogOut, Loader2, Database, Search, Key, LockOpen } from 'lucide-react';
import UserManagement from '../components/UserManagement';
import { useConfirm } from '../components/ConfirmProvider';

export default function SuperAdminDashboard() {
  const { user, logout } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Clean up any stale overlay when dialogs close
  useEffect(() => {
    if (!editDialogOpen && !createDialogOpen) {
      // Remove any stale overlay elements that might be stuck
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
    }
  }, [editDialogOpen, createDialogOpen]);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_person: '',
    contact_phone: '',
    plan: 'free',
    subscription_status: 'active',
    admin_password: '',
    wonde_enabled: false,
    wonde_api_key: ''
  });
  const [loading, setLoading] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);

  const handleUnlockLogin = async (e) => {
    e.preventDefault();
    if (!unlockEmail.trim()) return;
    setUnlockLoading(true);
    try {
      await api.superAdminUnlockLogin(unlockEmail.trim());
      toast.success(`Login lockout cleared for ${unlockEmail.trim()}`);
      setUnlockEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock user');
    } finally {
      setUnlockLoading(false);
    }
  };

  const fetchCustomers = useCallback(async () => {
    // Don't fetch if this is an impersonation session
    const isImpersonating = sessionStorage.getItem('impersonating') === 'true';
    if (isImpersonating) {
      return;
    }
    
    try {
      const data = await api.getCustomers();
      setCustomers(data);
    } catch (error) {
      // Only show error if user is super_admin and NOT impersonating
      const stillImpersonating = sessionStorage.getItem('impersonating') === 'true';
      if (user?.role === 'super_admin' && !stillImpersonating) {
        toast.error('Failed to load customers');
      }
    }
  }, [user?.role]);

  useEffect(() => {
    // Only fetch customers if user is actually a super_admin and NOT impersonating
    const isImpersonating = sessionStorage.getItem('impersonating') === 'true';
    if (user?.role === 'super_admin' && !isImpersonating) {
      fetchCustomers();
    }
  }, [user?.role, fetchCustomers]);

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name,
      contact_email: customer.contact_email || '',
      contact_person: customer.contact_person || '',
      contact_phone: customer.contact_phone || '',
      plan: customer.plan,
      subscription_status: customer.subscription_status || 'active',
      admin_password: '',
      wonde_enabled: customer.wonde_enabled || false,
      wonde_api_key: customer.wonde_api_key || ''
    });
    setEditDialogOpen(true);
  };

  const handleImpersonate = async (customerId) => {
    if (!(await confirm({
      title: 'Impersonate this customer admin?',
      description: 'A new tab will open signed in as this customer’s admin.',
      confirmText: 'Impersonate',
      destructive: false,
    }))) return;
    try {
      const response = await api.impersonateCustomer(customerId);
      
      // Open impersonation in a new tab with the token
      const impersonateUrl = `${window.location.origin}/impersonate?token=${response.token}`;
      window.open(impersonateUrl, '_blank');
      
      toast.success('Opened impersonation in new tab');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate');
    }
  };

  const handleSuspendCustomer = async (customerId) => {
    if (!(await confirm({
      title: 'Suspend this customer?',
      description: 'All of their users will be blocked from logging in.',
      confirmText: 'Suspend customer',
    }))) return;
    try {
      await api.suspendCustomer(customerId);
      toast.success('Customer suspended');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to suspend customer');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!(await confirm({
      title: 'Delete this customer?',
      description: 'This cannot be undone. Their tiles, users and settings will be removed.',
      confirmText: 'Delete customer',
    }))) return;
    try {
      await api.deleteCustomer(customerId);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.updateCustomer(selectedCustomer.id, formData);
      toast.success('Customer updated');
      setEditDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createCustomer(formData);
      // Close dialog first, then show success message
      setCreateDialogOpen(false);
      setFormData({
        name: '',
        contact_email: '',
        contact_person: '',
        contact_phone: '',
        plan: 'free',
        subscription_status: 'active',
        admin_password: ''
      });
      // Small delay to ensure dialog closes before fetching
      setTimeout(() => {
        toast.success('Customer created successfully');
        fetchCustomers();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">Welcome back, {user?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/activity-logs')}>
              <Activity className="w-4 h-4 mr-2" />
              Activity Logs
            </Button>
            <Button variant="outline" onClick={() => navigate('/admins')}>
              <Users className="w-4 h-4 mr-2" />
              Manage Admins
            </Button>
            <Button variant="ghost" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* User Management Section */}
        <div className="mb-8">
          <UserManagement customers={customers} />
        </div>

        {/* Unlock User Login */}
        <Card className="mb-6 border-orange-200 bg-orange-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <LockOpen className="w-5 h-5 text-orange-600" />
              Unlock User Login
            </CardTitle>
            <CardDescription>
              Clear the login lockout for a user who has been blocked after too many failed attempts. Lockout clears automatically after 5 minutes, but use this to unblock them instantly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUnlockLogin} className="flex gap-3 items-end">
              <div className="flex-1">
                <Label className="text-sm mb-1 block">User Email Address</Label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={unlockEmail}
                  onChange={(e) => setUnlockEmail(e.target.value)}
                  data-testid="unlock-email-input"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={unlockLoading || !unlockEmail.trim()}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="unlock-login-btn"
              >
                {unlockLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LockOpen className="w-4 h-4 mr-2" />}
                Unlock
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold">Customer Management</h2>
            <p className="text-muted-foreground mt-1">Manage all customer organizations</p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Customer
          </Button>
        </div>
        <div className="grid gap-4">
          {customers.map((customer) => (
            <Card key={customer.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {customer.name}
                      {customer.wonde_enabled && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          <Database className="w-3 h-3 mr-1" />
                          Wonde
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Plan: {customer.plan} | Status: {customer.subscription_status || 'active'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleImpersonate(customer.id)}
                    >
                      <UserCog className="w-4 h-4 mr-1" />
                      Impersonate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditCustomer(customer)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteCustomer(customer.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Portal URL - Easy to copy */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium mb-1">Portal URL (for end users)</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-white px-2 py-1 rounded border font-mono overflow-hidden text-ellipsis">
                      {window.location.origin}/portal/{customer.id}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/portal/${customer.id}`);
                        toast.success('Portal URL copied to clipboard!');
                      }}
                    >
                      Copy
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => window.open(`/portal/${customer.id}`, '_blank')}
                    >
                      Open
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Contact Person</p>
                    <p className="font-medium">{customer.contact_person || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium">{customer.contact_email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Phone</p>
                    <p className="font-medium">{customer.contact_phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="font-medium">{new Date(customer.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCustomer} className="space-y-4">
            <div className="space-y-2">
              <Label>Organization Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Person</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={formData.plan} onValueChange={(value) => setFormData({ ...formData, plan: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reset Admin Password (optional)</Label>
              <Input
                type="password"
                placeholder="Leave blank to keep current password"
                value={formData.admin_password}
                onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
              />
            </div>
            
            {/* Wonde Integration Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Wonde Integration (Schools)</Label>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Enable Wonde</p>
                    <p className="text-xs text-muted-foreground">Allow this customer to sync data from Wonde</p>
                  </div>
                  <Switch
                    checked={formData.wonde_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, wonde_enabled: checked })}
                  />
                </div>
                {formData.wonde_enabled && (
                  <div className="space-y-2">
                    <Label className="text-sm">Wonde API Token</Label>
                    <Input
                      type="password"
                      placeholder="Enter Wonde API token"
                      value={formData.wonde_api_key}
                      onChange={(e) => setFormData({ ...formData, wonde_api_key: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Get token from <a href="https://apps.wonde.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">apps.wonde.com</a>
                    </p>
                  </div>
                )}
                {selectedCustomer?.wonde_last_sync && (
                  <p className="text-xs text-muted-foreground">
                    Last synced: {new Date(selectedCustomer.wonde_last_sync).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCustomer} className="space-y-4">
            <div className="space-y-2">
              <Label>Organization Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Admin Email</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Admin Name</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Admin Password</Label>
              <Input
                type="password"
                value={formData.admin_password}
                onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={formData.plan} onValueChange={(value) => setFormData({ ...formData, plan: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
